import { useMemo, useCallback } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';

import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { Constants, CountryUtils, AccountUtils, CurrencyUtils } from '@bingads-webui-clientcenter/entity-utils';
import {
  getAccountPermission,
  getCustomerPermission,
  findCountries,
  findCurrencies,
  getAllowedCountriesByAccountModeMapping,
} from '@bingads-webui-clientcenter/repository-utils';

import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';

import { useDataManagement } from './';
import { Forms } from '../constants';
import { initialAblInfoStateGetters } from '../utils';

const {
  SecurityAction, AccountMode, CustomerUnsupportedBusinessLocations, UnsupportedBusinessLocations, CountryCode, HKStateCode,
} = Constants;
const { extendAccount, isUnifiedSmartAccount } = AccountUtils;
const {
  getAvailableCountriesByCurrency,
  getAvailableCountriesByAccountMode,
} = CountryUtils;
const { getAvailableCurrenciesByCountryCode } = CurrencyUtils;
const { getInitialCountryCode, getInitialPrefillAddressId } = initialAblInfoStateGetters;

export const useData = ({
  dataManagement,
  customerId,
  accountId,
  accountIds,
  countryCode,
  userCountryCode,
  initialAblInfo,
  formControls,
  isAccountCurrencyFixed,
  excludeCustomerUnsupportedBusinessLocations,
  excludeUnsupportedBusinessLocations,
  getABLVerificationStatus,
  showBillingGroupAlert,
  isReadonly,
  isEnhancedABLP1Enabled,
  bypassAccountCurrencyFilter,
  shouldSupportMoreCountriesForXandr,
  isMoveHKToCountryEnabled,
  onlyIncludeBusinessAIVAddresses,
}) => {
  const jsDataContext = useJsDataContext({ isStrictMode: false });
  const hasAccountData = useMemo(() => accountId > 0 || _.isFunction(_.get(dataManagement, ['account', 'dataFetch'])), [accountId, dataManagement]);

  const { data: countryList = [], loading: countryLoading } = useDataManagement({
    dataFetch: findCountries,
    ...dataManagement.countries,
  }, [dataManagement.countries]);

  const { data: currencies, loading: currencyLoading } = useDataManagement({
    dataFetch: jsData => (hasAccountData
      ? findCurrencies(jsData)
      : Promise.resolve(null)),
    ...dataManagement.currencies,
  }, [hasAccountData, dataManagement.currencies]);

  const { data: currencyCountriesMapping, loading: currencyCountriesMappingLoading } = useDataManagement({
    dataFetch: jsData => (hasAccountData
      ? jsData.CurrencyCountriesMapping.findAll()
      : Promise.resolve(null)),
    ...dataManagement.currencyCountriesMapping,
  }, [hasAccountData, dataManagement.currencyCountriesMapping]);

  const { data: allowedCountriesByAccountModeMapping, loading: allowedCountriesByAccountModeMappingLoading } = useDataManagement({
    dataFetch: jsData => (hasAccountData
      ? getAllowedCountriesByAccountModeMapping(jsData)
      : Promise.resolve(null)),
    ...dataManagement.allowedCountriesByAccountModeMapping,
  }, [hasAccountData, dataManagement.allowedCountriesByAccountModeMapping]);

  const { data: provinceList = [], loading: provinceLoading } = useDataManagement({
    dataFetch: jsData => (_.isString(countryCode) && !_.isEmpty(countryCode)
      ? jsData.DomainDataSubGeography.findAll({ countryCode })
      : Promise.resolve([])),
    ...dataManagement.subGeography,
    callback: (provinces) => {
      // remove HK from China province list when Move HK to Country feature is enabled
      if (isMoveHKToCountryEnabled && countryCode === CountryCode.CN) {
        return _.reject(provinces, ({ Key }) => Key === HKStateCode);
      }

      return provinces;
    },
  }, [countryCode, dataManagement.subGeography]);

  const { data: userProvinceList = [], loading: userProvinceLoading } = useDataManagement({
    dataFetch: jsData => (_.isString(userCountryCode)
      ? jsData.DomainDataSubGeography.findAll({ countryCode: userCountryCode })
      : Promise.resolve([])),
    ...dataManagement.userAddressSubGeography,
  }, [userCountryCode, dataManagement.userAddressSubGeography]);

  const { data: addressList = [], loading: addressLoading } = useDataManagement({
    dataFetch: (jsData) => {
      let promise;
      const initialPrefillAddressId = getInitialPrefillAddressId(initialAblInfo);
      if (accountId > 0) {
        promise = jsData.Account.find(accountId).then(account => jsData.CustomerAddress.findAll({
          customerId: account.ParentCustomerId,
          getABLVerificationStatus,
          includeAddressId: isEnhancedABLP1Enabled ? initialPrefillAddressId : null,
          includeBusinessAIVAddresses: onlyIncludeBusinessAIVAddresses,
        }));
      } else if (customerId > 0) {
        promise = jsData.CustomerAddress.findAll({
          customerId,
          getABLVerificationStatus,
          includeAddressId: isEnhancedABLP1Enabled ? initialPrefillAddressId : null,
          includeBusinessAIVAddresses: onlyIncludeBusinessAIVAddresses,
        });
      } else {
        promise = Promise.resolve([]);
      }
      return promise.then(rawAddressList => _.filter(rawAddressList, AddressUtils.isNotEmpty));
    },
    ...dataManagement.address,
  }, [customerId, accountId, dataManagement.address, onlyIncludeBusinessAIVAddresses]);

  const { data: accountData = null, loading: accountLoading } = useDataManagement({
    dataFetch: jsData => (accountId > 0
      ? jsData.Account.find(accountId)
      : Promise.resolve(null)),
    ...dataManagement.account,
  }, [accountId, dataManagement.account]);

  const { data: accountList = null, loading: accountListLoading } = useDataManagement({
    dataFetch: jsData => (!_.isEmpty(accountIds)
      ? jsData.Account.findAll({ accountIds })
      : Promise.resolve(null)),
    ...dataManagement.accounts,
  }, [accountIds, dataManagement.accounts]);

  const { data: accountPermissions = {} } = useDataManagement({
    dataFetch: jsData => (accountId > 0
      ? getAccountPermission(jsData, { accountId, securityActions: [SecurityAction.AccountUpdate] })
      : Promise.resolve({})),
    ...dataManagement.accountPermissions,
  }, [accountId, dataManagement.accountPermissions]);

  const { data: customerPermissions = {}, loading: customerPermissionsLoading } = useDataManagement({
    dataFetch: jsData => getCustomerPermission(jsData, { customerId: customerId > 0 ? customerId : 0, securityActions: [SecurityAction.TaxExemptionCertStatusSet, SecurityAction.CustomerUpdate] }),
    ...dataManagement.customerPermissions,
  }, [customerId, dataManagement.customerPermissions]);

  const { data: accountBillingGroupMap = null } = useDataManagement({
    dataFetch: (jsData) => {
      let AccountIds = [];
      if (!_.isEmpty(accountIds)) {
        AccountIds = accountIds;
      } else if (accountId > 0) {
        AccountIds = [accountId];
      }
      return (customerPermissions[SecurityAction.CustomerUpdate] && showBillingGroupAlert && !isReadonly && !_.isEmpty(AccountIds))
        ? jsData.BillingGroupAccount.findAll({ AccountIds })
        : Promise.resolve(null);
    },
    ...dataManagement.accountBillingGroupMap,
  }, [accountId, accountIds, showBillingGroupAlert, isReadonly, dataManagement.accountBillingGroupMap, customerPermissions[SecurityAction.CustomerUpdate]]);

  // if there is account data, we need to find available countries based on currencies, currencyCountriesMapping, allowedCountriesByAccountModeMappingLoading and accountData
  const isBusinessLocationDependencyLoading = countryLoading || (hasAccountData && (currencyLoading || currencyCountriesMappingLoading || allowedCountriesByAccountModeMappingLoading || accountLoading || accountListLoading));

  const extendedAccount = useMemo(() => extendAccount(accountData), [accountData]);

  const businessLocationList = useMemo(() => {
    if (isBusinessLocationDependencyLoading) {
      return [];
    }

    const { CurrencyType: currencyType } = bypassAccountCurrencyFilter ? {} : (((isAccountCurrencyFixed || extendedAccount.isCreated()) && extendedAccount) || {});
    let { AccountMode: accountMode } = extendedAccount || {};
    const initialCountryCode = getInitialCountryCode(initialAblInfo);

    if (!_.isEmpty(accountList) && _.some(accountList, isUnifiedSmartAccount)) {
      accountMode = AccountMode.UnifiedSmart;
    }

    let countries = getAvailableCountriesByAccountMode({
      countries: countryList, accountMode, allowedCountriesByAccountModeMapping,
    });

    if (excludeCustomerUnsupportedBusinessLocations) {
      countries = _.reject(countries, country => _.contains(CustomerUnsupportedBusinessLocations, country.Code));
    }

    if (excludeUnsupportedBusinessLocations) {
      countries = _.reject(countries, country => _.contains(UnsupportedBusinessLocations, country.Code));
    }

    const countryCodeKeepList = [initialCountryCode];

    // when Move HK to Country feature is enabled, need to keep HK in the country list
    // TODO: remove HK from YahooMarkets constant while removing Yahoo markets after this pilot GA
    if (isMoveHKToCountryEnabled) {
      countryCodeKeepList.push(CountryCode.HK);
    }

    // for Xandr ABL collection, it has no controlled currency country and support TW, should keep TW and not removing it
    if (shouldSupportMoreCountriesForXandr) {
      countryCodeKeepList.push(CountryCode.TW, CountryCode.HK);
    }

    return getAvailableCountriesByCurrency({
      countries, currencyCountriesMapping, currencies, currencyType, countryCodeKeepList,
    });
  }, [isBusinessLocationDependencyLoading, bypassAccountCurrencyFilter, isAccountCurrencyFixed, extendedAccount, initialAblInfo, accountList, countryList, allowedCountriesByAccountModeMapping, excludeCustomerUnsupportedBusinessLocations, excludeUnsupportedBusinessLocations, isMoveHKToCountryEnabled, shouldSupportMoreCountriesForXandr, currencyCountriesMapping, currencies]);

  const { data: timeZones = [], loading: timeZonesLoading } = useDataManagement({
    dataFetch: jsData => (formControls[Forms.TimeZone].visible
      ? jsData.DomainDataTimeZone.findAll().then(timezones => _.map(timezones, timezone => ({ Value: timezone.id, Text: timezone.value.Name })))
      : Promise.resolve(null)
    ),
    ...dataManagement.timeZones,
  }, [formControls[Forms.TimeZone].visible, dataManagement.timeZones]);

  const { data: timeZonesPerCountryMapping = [], loading: timeZonesPerCountryMappingLoading } = useDataManagement({
    dataFetch: jsData => (formControls[Forms.TimeZone].visible
      ? jsData.CountryTimeZonesMapping.findAll()
      : Promise.resolve(null)),
    ...dataManagement.timeZonesPerCountryMapping,
  }, [formControls[Forms.TimeZone].visible, dataManagement.timeZonesPerCountryMapping]);

  const timeZoneDataLoading = timeZonesLoading || timeZonesPerCountryMappingLoading;

  const { data: countryCurrenciesMapping, loading: countryCurrenciesMappingLoading } = useDataManagement({
    dataFetch: jsData => (formControls[Forms.CurrencySelector].visible
      ? jsData.CountryCurrenciesMapping.findAll()
      : Promise.resolve(null)),
    ...dataManagement.countryCurrenciesMapping,
  }, [formControls[Forms.CurrencySelector].visible, dataManagement.countryCurrenciesMapping]);

  const currencyDataLoading = currencyLoading || countryCurrenciesMappingLoading;

  const currencyList = useMemo(() => (currencyDataLoading ? [] : getAvailableCurrenciesByCountryCode({ countryCode, currencies, countryCurrenciesMapping })), [countryCode, countryCurrenciesMapping, currencies, currencyDataLoading]);

  // Returns the currencies supported by an arbitrary country code (not just the
  // currently selected one). Lets callers validate whether an already-selected
  // currency remains valid for a newly picked country before deciding to clear it.
  const getCurrenciesForCountryCode = useCallback(
    countryCodeParam => getAvailableCurrenciesByCountryCode({ countryCode: countryCodeParam, currencies, countryCurrenciesMapping }),
    [currencies, countryCurrenciesMapping]
  );

  // Fetches the state/province list for an arbitrary country code on demand.
  // The prefetched `provinceList` above is keyed on the currently selected
  // `countryCode` and is momentarily empty while a country change is in flight,
  // which races with the AddressForm reading its options exactly once per
  // country change. Fetching on demand for the requested country resolves the
  // correct list regardless of the prefetch timing. (LocationAndCurrency)
  const getStatesForCountryCode = useCallback(
    (countryCodeParam) => {
      if (!jsDataContext || !_.isString(countryCodeParam) || _.isEmpty(countryCodeParam)) {
        return Promise.resolve([]);
      }
      return Promise.resolve(jsDataContext.DomainDataSubGeography.findAll({ countryCode: countryCodeParam }))
        .then(provinces => (isMoveHKToCountryEnabled && countryCodeParam === CountryCode.CN
          ? _.reject(provinces, ({ Key }) => Key === HKStateCode)
          : provinces));
    },
    [jsDataContext, isMoveHKToCountryEnabled]
  );

  return {
    countryList,
    businessLocationList,
    provinceList,
    userProvinceList,
    addressList,
    accountData: extendedAccount,
    accountPermissions,
    customerPermissions,
    accountBillingGroupMap,
    accountLoading,
    provinceLoading,
    userProvinceLoading,
    addressLoading,
    customerPermissionsLoading,
    timeZones,
    timeZonesPerCountryMapping,
    timeZoneDataLoading,
    currencyList,
    currencyDataLoading,
    getCurrenciesForCountryCode,
    getStatesForCountryCode,
  };
};
